/**
 * Internal event bus — semantic cross-cutting signals only.
 *
 * Bus events are NOT the analytics wire protocol. Analytics events go through
 * `core/analytics/track.ts` directly; the bus carries signals that more than
 * one part of the SDK (or the host page) needs to react to.
 */

import type { ResolvedIds } from '../identity';
import type { ConsentStatus } from '../consent/engine';

export enum SdkEvent {
  /** Init failed (config fetch or consent phase). */
  INIT_ERROR = 'init_error',
  /** Consent status transitioned between granted/denied. */
  CONSENT_CHANGED = 'consent_changed',
  /** Identity (uid/sid) was re-resolved, e.g. after a consent flip. */
  IDENTITY_REFRESHED = 'identity_refreshed',
  /** Page view dispatched (pixels listen). */
  PAGE_VIEW = 'page_view',
  /** An ecom affiliate clickout was accepted (pixels listen). */
  ECOM_CLICKOUT = 'ecom_clickout',
  /** Fired just before the SDK navigates away (ecom clickout). */
  BEFORE_NAVIGATE = 'before_navigate',
  /** An ad bid was counted into the session value (yoto `SESSION_BID_CHANGE`). */
  SESSION_BID_CHANGE = 'session_bid_change',
  /** The session value crossed a cent milestone (yoto `SESSION_BID_CENT_INCREMENT`). */
  SESSION_BID_CENT_INCREMENT = 'session_bid_cent_increment',
}

export interface InitErrorPayload {
  error: Error;
  phase: 'config' | 'consent' | 'unknown';
}

export interface ConsentChangedPayload {
  status: 'granted' | 'denied';
  prev: ConsentStatus;
}

/**
 * Cross-module clickout signal — the subset of the ecom clickout the pixels
 * module needs. The full analytics payload stays inside the ecom module.
 */
export interface EcomClickoutSignal {
  url: string;
  /** Amazon ASIN when the click targets a product detail page. */
  productId?: string | number;
  /** ASIN or full general-link URL (never a subtag/click id). */
  advertiserCampaignId?: string;
  ecomProductMarket?: string;
  /** True when this is the first unique ecom click of the browser session (`eafuc`). */
  unique: boolean;
  /** 1-based session click count after this click. */
  sessionClicks: number;
}

export interface BeforeNavigatePayload {
  url: string;
}

/** All three are CPM sums, not currency — divide by 1000 for revenue. */
export interface SessionBidChangePayload {
  /** Accumulated over this page load. */
  pageCpm: number;
  /** Accumulated over the browser session, persisted in `sessionStorage`. */
  sessionCpm: number;
  /** The bid just counted. */
  bidCpm: number;
}

export interface EventPayloads {
  [SdkEvent.INIT_ERROR]: InitErrorPayload;
  [SdkEvent.CONSENT_CHANGED]: ConsentChangedPayload;
  [SdkEvent.IDENTITY_REFRESHED]: ResolvedIds;
  [SdkEvent.PAGE_VIEW]: void;
  [SdkEvent.ECOM_CLICKOUT]: EcomClickoutSignal;
  [SdkEvent.BEFORE_NAVIGATE]: BeforeNavigatePayload;
  [SdkEvent.SESSION_BID_CHANGE]: SessionBidChangePayload;
  /** The milestone reached, in cents. */
  [SdkEvent.SESSION_BID_CENT_INCREMENT]: number;
}

export type Unsubscribe = () => void;

export type EmitArgs<K extends SdkEvent> = EventPayloads[K] extends void
  ? []
  : [payload: EventPayloads[K]];

type Listener = (payload: unknown) => void;

const listeners = new Map<SdkEvent, Set<Listener>>();

export function on<K extends SdkEvent>(
  event: K,
  listener: (payload: EventPayloads[K]) => void,
): Unsubscribe {
  const set = listeners.get(event) ?? new Set<Listener>();
  listeners.set(event, set);
  // Single cast boundary: the payload map guarantees listener/emit agreement.
  const entry = listener as Listener;
  set.add(entry);
  return () => {
    set.delete(entry);
  };
}

export function emit<K extends SdkEvent>(event: K, ...args: EmitArgs<K>): void {
  const set = listeners.get(event);
  if (!set || set.size === 0) return;
  for (const listener of [...set]) {
    try {
      listener(args[0]);
    } catch (err) {
      if (typeof console !== 'undefined') console.error('[kueez-sdk] listener threw', err);
    }
  }
}

/** Test-only — clear all listeners. */
export function _resetBus(): void {
  listeners.clear();
}
